import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        primary:
          "bg-gradient-to-b from-orange-400 to-orange-600 text-white font-bold px-8 py-4 rounded-lg text-base space-x-3 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-space-grotesk tracking-wide uppercase btn-primary-filter justify-center border border-transparent",
        secondary:
          "bg-white border border-gray-200 text-gray-800 font-bold px-8 py-4 rounded-lg text-base space-x-3 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-space-grotesk tracking-wide uppercase btn-secondary-shadow justify-center ",
        destructive:
          "bg-gradient-to-b from-red-500 to-red-700 text-white font-bold px-8 py-4 rounded-lg text-base space-x-3 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-space-grotesk tracking-wide uppercase btn-destructive-filter justify-center border border-transparent hover:from-red-600 hover:to-red-800",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "!h-9 rounded-md !px-3 !text-sm",
        md: "!h-auto !px-4 !py-2 btn-md-font",
        lg: "h-11 rounded-md px-8",
        primary: "!h-auto",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
